import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Inbox, 
  Mic, 
  Calendar,
  CalendarClock,
  Users, 
  Church,
  Home,
  Image,
  LogOut,
  FileText,
  Quote,
  Settings,
  UserCog,
  Mail,
  MailCheck,
  Send,
  Video,
  Heart,
  UserCheck,
  QrCode,
  Menu,
  X,
  Bell,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  User,
  ShieldCheck,
  CreditCard,
  Activity,
  BookOpen,
  DollarSign,
  BarChart3
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Navigation sections for better organization
const navSections = [
  {
    title: 'Main',
    items: [
      { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
      { name: 'Submissions', href: '/admin/submissions', icon: Inbox },
    ]
  },
  {
    title: 'Admin Panel',
    items: [
      { name: 'Overview', href: '/admin/panel', icon: Activity },
      { name: 'Admin Users', href: '/admin/panel/users', icon: ShieldCheck },
      { name: 'Partners', href: '/admin/panel/partners', icon: Users },
      { name: 'Transactions', href: '/admin/panel/transactions', icon: CreditCard },
      { name: 'Knowledge Base', href: '/admin/panel/knowledge-base', icon: BookOpen },
      { name: 'Audit Logs', href: '/admin/panel/audit-logs', icon: Activity },
    ]
  },
  {
    title: 'Ministry Tools',
    items: [
      { name: 'Prayer Team', href: '/admin/prayer-team', icon: Heart },
      { name: 'Volunteer Scheduling', href: '/admin/volunteers', icon: UserCheck },
      { name: 'QR Code Generator', href: '/admin/qr-codes', icon: QrCode },
    ]
  },
  {
    title: 'Communication',
    items: [
      { name: 'Staff & Notifications', href: '/admin/staff', icon: UserCog },
      { name: 'Campaign Hub', href: '/admin/campaigns', icon: Send },
      { name: 'Message Templates', href: '/admin/templates', icon: Mail },
      { name: 'Email Delivery Logs', href: '/admin/notification-logs', icon: MailCheck },
    ]
  },
  {
    title: 'Content',
    items: [
      { name: 'Page Editor', href: '/admin/pages', icon: FileText },
      { name: 'Media Library', href: '/admin/media', icon: Image },
      { name: 'Sermons', href: '/admin/sermons', icon: Mic },
      { name: 'Events', href: '/admin/events', icon: Calendar },
      { name: 'Event Templates', href: '/admin/event-templates', icon: CalendarClock },
      { name: 'Ministries', href: '/admin/ministries', icon: Church },
      { name: 'Team', href: '/admin/team', icon: Users },
      { name: 'Watch Live', href: '/admin/live-engagement', icon: Video },
      { name: 'Live Features', href: '/admin/live-enhanced', icon: Activity },
      { name: 'Live Management', href: '/admin/live-management', icon: BarChart3 },
      { name: 'Testimonials', href: '/admin/testimonials', icon: Quote },
    ]
  },
];

const bottomNav = [
  { name: 'Settings', href: '/admin/settings', icon: Settings },
  { name: 'Help & Support', href: '/admin/help', icon: HelpCircle },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [adminUser, setAdminUser] = useState({ name: 'Admin', email: '' });

  useEffect(() => {
    // Check authentication status from both sessionStorage and localStorage
    const sessionAuth = sessionStorage.getItem('htccc_admin_auth');
    const localAuth = localStorage.getItem('htccc_admin_auth');
    const authData = sessionAuth || localAuth;
    
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        // Check if auth is still valid (24 hour expiry)
        if (parsed.authenticated && (Date.now() - parsed.timestamp) < 24 * 60 * 60 * 1000) {
          setIsAuthenticated(true);
          setAdminUser({ name: parsed.email?.split('@')[0] || 'Admin', email: parsed.email || '' });
        } else {
          // Clear expired auth data
          sessionStorage.removeItem('htccc_admin_auth');
          localStorage.removeItem('htccc_admin_auth');
          navigate('/admin/login');
        }
      } catch {
        // Clear invalid auth data
        sessionStorage.removeItem('htccc_admin_auth');
        localStorage.removeItem('htccc_admin_auth');
        navigate('/admin/login');
      }
    } else {
      navigate('/admin/login');
    }
    setIsLoading(false);
  }, [navigate]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Load sidebar collapsed state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('htccc_sidebar_collapsed');
    if (saved) setSidebarCollapsed(JSON.parse(saved));
  }, []);

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('htccc_sidebar_collapsed', JSON.stringify(newState));
  };

  const handleLogout = () => {
    // Clear all auth data on logout
    sessionStorage.removeItem('htccc_admin_auth');
    localStorage.removeItem('htccc_admin_auth');
    localStorage.removeItem('htccc_admin_remember');
    navigate('/admin/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ht-red"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const SidebarContent = ({ mobile = false }) => (
    <>
      {/* Logo Section */}
      <div className={cn(
        "flex items-center border-b border-gray-800 transition-all",
        sidebarCollapsed && !mobile ? "p-4 justify-center" : "p-4 gap-3"
      )}>
        <div className="w-10 h-10 bg-ht-red rounded-lg flex items-center justify-center flex-shrink-0">
          <Church className="w-6 h-6 text-white" />
        </div>
        {(!sidebarCollapsed || mobile) && (
          <div>
            <h1 className="text-lg font-bold text-white">HT Admin</h1>
            <p className="text-xs text-gray-400">Holy Temple CCC</p>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navSections.map((section, idx) => (
          <div key={section.title} className={cn(idx > 0 && "mt-4")}>
            {(!sidebarCollapsed || mobile) && (
              <p className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {section.title}
              </p>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-all",
                    isActive 
                      ? "bg-ht-red text-white" 
                      : "text-gray-400 hover:text-white hover:bg-gray-800",
                    sidebarCollapsed && !mobile && "justify-center px-2"
                  )}
                  title={sidebarCollapsed && !mobile ? item.name : undefined}
                  data-testid={`admin-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {(!sidebarCollapsed || mobile) && (
                    <span className="text-sm">{item.name}</span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-gray-800 py-4">
        {bottomNav.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-all",
                isActive 
                  ? "bg-ht-red text-white" 
                  : "text-gray-400 hover:text-white hover:bg-gray-800",
                sidebarCollapsed && !mobile && "justify-center px-2"
              )}
              title={sidebarCollapsed && !mobile ? item.name : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {(!sidebarCollapsed || mobile) && (
                <span className="text-sm">{item.name}</span>
              )}
            </Link>
          );
        })}
        
        <Link
          to="/"
          className={cn(
            "flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-all text-gray-400 hover:text-white hover:bg-gray-800",
            sidebarCollapsed && !mobile && "justify-center px-2"
          )}
          title={sidebarCollapsed && !mobile ? "Back to Site" : undefined}
        >
          <Home className="w-5 h-5 flex-shrink-0" />
          {(!sidebarCollapsed || mobile) && <span className="text-sm">Back to Site</span>}
        </Link>
        
        <button
          onClick={handleLogout}
          className={cn(
            "flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-all w-[calc(100%-16px)] text-red-400 hover:text-red-300 hover:bg-red-900/30",
            sidebarCollapsed && !mobile && "justify-center px-2"
          )}
          title={sidebarCollapsed && !mobile ? "Sign Out" : undefined}
          data-testid="admin-logout"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {(!sidebarCollapsed || mobile) && <span className="text-sm">Sign Out</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Desktop Sidebar */}
      <aside 
        className={cn(
          "hidden lg:flex flex-col fixed left-0 top-0 h-full bg-gray-900 transition-all duration-300 z-40",
          sidebarCollapsed ? "w-20" : "w-64"
        )}
      >
        <SidebarContent />
        
        {/* Collapse Toggle */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-3 top-20 w-6 h-6 bg-gray-900 border border-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          data-testid="sidebar-toggle"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={cn(
          "lg:hidden fixed left-0 top-0 h-full w-64 bg-gray-900 z-50 transform transition-transform duration-300 flex flex-col",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>
        <SidebarContent mobile />
      </aside>

      {/* Main Content Area */}
      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300",
        sidebarCollapsed ? "lg:ml-20" : "lg:ml-64"
      )}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 lg:px-8 h-16 flex items-center justify-between">
          {/* Left: Mobile Menu + Breadcrumb */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="hidden sm:block">
              <h2 className="text-lg font-semibold text-gray-800">
                {navSections.flatMap(s => s.items).find(i => i.href === location.pathname)?.name || 
                 bottomNav.find(i => i.href === location.pathname)?.name || 
                 'Dashboard'}
              </h2>
            </div>
          </div>

          {/* Right: Notifications + User Menu */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowUserMenu(false);
                }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg relative"
                data-testid="notifications-toggle"
              >
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
              
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="font-semibold">Notifications</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No new notifications
                      </div>
                    ) : (
                      notifications.map((notif, idx) => (
                        <div key={idx} className="p-3 border-b border-gray-50 hover:bg-gray-50">
                          <p className="text-sm">{notif.message}</p>
                          <p className="text-xs text-gray-400 mt-1">{notif.time}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setShowNotifications(false);
                }}
                className="flex items-center gap-2 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                data-testid="user-menu-toggle"
              >
                <div className="w-8 h-8 bg-ht-red rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="hidden sm:block text-sm font-medium">{adminUser.name}</span>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  <div className="p-3 border-b border-gray-100">
                    <p className="font-medium text-sm">{adminUser.name}</p>
                    <p className="text-xs text-gray-500">{adminUser.email}</p>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/admin/settings"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </Link>
                    <Link
                      to="/"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Home className="w-4 h-4" />
                      View Site
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
        <div className="flex justify-around">
          {[
            { name: 'Home', href: '/admin', icon: LayoutDashboard },
            { name: 'Inbox', href: '/admin/submissions', icon: Inbox },
            { name: 'Events', href: '/admin/events', icon: Calendar },
            { name: 'More', href: '#', icon: Menu, onClick: () => setMobileMenuOpen(true) },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return item.onClick ? (
              <button
                key={item.name}
                onClick={item.onClick}
                className={cn(
                  "flex flex-col items-center py-2 px-4 text-xs",
                  "text-gray-500"
                )}
              >
                <Icon className="w-5 h-5 mb-1" />
                {item.name}
              </button>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex flex-col items-center py-2 px-4 text-xs",
                  isActive ? "text-ht-red" : "text-gray-500"
                )}
              >
                <Icon className="w-5 h-5 mb-1" />
                {item.name}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
